import { useEffect } from 'react';
import BackendService from 'BackendService';
import { CrossDeviceAuthData, PairingStatus } from './types';

export function usePairingPolling(
  authData: CrossDeviceAuthData | null,
  setPairingStatus: (status: PairingStatus) => void,
  setShowApproval: (show: boolean) => void,
  setCorrectChoice: (choice: string) => void,
  setDeviceInfo: (info: any) => void,
  setTimeLeft: (time: number) => void,
  setError: (error: string | null) => void,
) {
  useEffect(() => {
    if (!authData) return;

    // Countdown timer for expiration
    const countdownInterval = setInterval(() => {
      const now = Date.now();
      const expires = new Date(authData.expiresAt).getTime();
      const remaining = Math.max(0, Math.floor((expires - now) / 1000));

      setTimeLeft(remaining);

      if (remaining === 0) {
        setError('QR code has expired. Please regenerate.');
        setPairingStatus('expired');
      }
    }, 1000);

    // Polling for pairing status
    const statusPollingInterval = setInterval(async () => {
      try {
        const backendService = new BackendService();
        const status = await backendService.getPairingStatus(authData.pairingId);

        console.log('Desktop polling - pairing status:', status);
        setPairingStatus(status.status);

        if (status.status === 'scanned') {
          setShowApproval(true);
          if (status.deviceInfo) {
            setDeviceInfo(status.deviceInfo);
          }
        } else if (status.status === 'challenge_required' && status.correctChoice) {
          setCorrectChoice(status.correctChoice);
          setShowApproval(true); // Keep approval screen visible with challenge choice
        } else if (status.status === 'challenge_verified') {
          console.log('Challenge verified successfully!');
        } else if (status.status === 'redeemed') {
          console.log('Pairing redeemed successfully - login complete!');
          // Stop polling once login is complete
          clearInterval(statusPollingInterval);
          clearInterval(countdownInterval);
        } else if (status.status === 'denied') {
          // Also stop polling if denied
          clearInterval(statusPollingInterval);
          clearInterval(countdownInterval);
        }
      } catch (error) {
        console.error('Failed to check pairing status:', error);
      }
    }, 2000); // Poll every 2 seconds

    return () => {
      clearInterval(countdownInterval);
      clearInterval(statusPollingInterval);
    };
  }, [
    authData,
    setPairingStatus,
    setShowApproval,
    setCorrectChoice,
    setDeviceInfo,
    setTimeLeft,
    setError,
  ]);
}
